import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import useBundleTranslation from 'i18n';
import { DenyPageAPI } from 'api/api';
import styles from './DenyPage.styles';
import { useParams } from 'react-router-dom';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { setPageTitle } from 'store/pageTitleSlice';
import { useConfigConstantValue } from 'hooks/useConfigConstant';
import { AccessRequestElementInfo, homePage } from 'api/home-page';
import PageErrorBlock from 'components/common/error/PageErrorBlock';
import DiscoverableDenyPage from './discoverable';

interface denyPageData {
    info?: any;
}
const DenyPage = (props: any) => {
    const { t } = useBundleTranslation(['app/auth/deny']);
    const urlParams = useParams();
    const dispatch = useDispatch();
    const [type, setType] = useState<string>();
    const [isElement, setIsElement] = useState<boolean>(false);
    const [mainTitle, setMainTitle] = useState<string>('');
    const [isSentRequest, setIsSentRequest] = useState<boolean>(false);
    const [sentRequestMessage, setSentRequestMessage] = useState<string>('');

    const okButtonText = useConfigConstantValue('ACCESS_REQUEST_BUTTON_TEXT');
    const accessRequestUrl = useConfigConstantValue('ACCESS_REQUEST_URL');
    const accessRequestViaWebpage = useConfigConstantValue('ACCESS_REQUEST_VIA_WEBPAGE');
    let urlData: any = Object.fromEntries(new URLSearchParams(document.location.search));
    let linkText = okButtonText || null;
    const componentPageInfo = props?.info;

    useEffect(() => {
        let typeValue = componentPageInfo ? props.type : urlParams?.type;
        if ((undefined == typeValue || '' == typeValue) && urlData.type && urlData.type > '') typeValue = urlData.type;
        setType(typeValue ?? '');

        setIsElement(typeValue === 'permission' && urlData.entity === 'DashboardElementView');
    }, []);

    useEffect(() => {
        const newMainTitle =
            'notfound' === type || 'not_found' === type
                ? 'notfound'
                : type === 'not_have_data_dataset'
                  ? 'dataset_no_data_title'
                  : 'title';
        setMainTitle(newMainTitle);
        dispatch(setPageTitle(t(newMainTitle)));
    }, [type]);

    const { data: pageData, refetch } = useQuery<denyPageData, Error>({
        queryKey: ['accessDenyPage'],
        queryFn: () => {
            return DenyPageAPI.getData({ type: type, data: urlData });
        },
        enabled: !componentPageInfo && type !== undefined,
    });

    const queryKey = `element_preview_deny_${Number(urlData.entityId)}_${Number(urlData.segmentId)}`;

    const { data: accessRequestInfoData } = useQuery<AccessRequestElementInfo, Error>({
        queryKey: [queryKey],
        queryFn: () => {
            return homePage.getAccessRequestInfo(Number(urlData.entityId), Number(urlData.segmentId));
        },
        enabled: isElement,
    });

    let errorInfo: any = componentPageInfo ?? pageData?.info;

    if (!errorInfo) return <LoadingPlaceholder variant={'secondary'} />;

    if (errorInfo && errorInfo['isDiscoverable'] && errorInfo['isDiscoverable'] == 'Y') {
        return (
            <Box sx={{ pt: 4, maxWidth: '1128px', mx: 'auto' }}>
                <DiscoverableDenyPage
                    elementId={Number(errorInfo['entity']['elementId'])}
                    segmentValueId={Number(errorInfo['entity']['segmentValueId'])}
                    //errorMessage={errorInfo.message}
                />
            </Box>
        );
    }

    const sendRequestLink = () => {
        if (accessRequestViaWebpage && accessRequestUrl) {
            window.open(accessRequestUrl, '_blank');
            return;
        }

        DenyPageAPI.requireTilesAccess(errorInfo.entity.elementId ?? 0, errorInfo.entity.segmentValueId ?? 0)
            .then((response) => {
                if (response.data?.status === 'OK') {
                    setIsSentRequest(true);
                    setSentRequestMessage(response.data.message);
                }
            })
            .catch(() => {});
    };

    const inIframe = (() => {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    })();

    if (errorInfo && errorInfo.message != '' && 'Y' == errorInfo.fullPageMessage)
        return <Box sx={styles.mainWrapper} dangerouslySetInnerHTML={{ __html: errorInfo.message }}></Box>;

    return (
        <Box sx={styles.mainWrapper}>
            <PageErrorBlock
                errorType={type}
                errorTitle={t(mainTitle)}
                errorMessage={errorInfo.message}
                errorCssClass={errorInfo.cssClass}
                errorList={
                    !urlParams.error ? undefined : Array.isArray(urlParams.error) ? urlParams.error : [urlParams.error]
                }
                showBackHomeBtn={!inIframe && !urlData.home_unavailable}
                additionalButtonsSlot={
                    'permission' == type ? (
                        <>
                            {isSentRequest ? (
                                <Box sx={{ color: 'error.main', pl: 1, lineHeight: 1.2 }}>{sentRequestMessage}</Box>
                            ) : (
                                <>
                                    {linkText && (
                                        <>
                                            {!inIframe && (
                                                <>
                                                    {' '}
                                                    <Box sx={{ pl: 1.5 }}>{t('or_link_text')}</Box>
                                                </>
                                            )}
                                            <Button
                                                data-test={'send-request-button'}
                                                onClick={sendRequestLink}
                                                variant={!inIframe ? 'text' : undefined}
                                            >
                                                {linkText}
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    ) : undefined
                }
                iconImageUrl={accessRequestInfoData?.accessData?.image}
            />
        </Box>
    );
};

export default DenyPage;
